import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import { PricingSheetData, ScopeSummaryLine } from '../utils/pricingSheetData';
import { buildAssumptionSections } from '../utils/proposalAssumptions';
import logo from '../../../assets/images/CRCEvansLogo.png';

const BRAND_RED = '#EB1C37';
const TEXT_DARK = '#00273F';
const TEXT_GRAY = '#464646';
const BORDER_GRAY = '#E2E5EA';
const BG_LIGHT = '#F5F6F8';
const BG_ALT_ROW = '#EEF0F3';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: TEXT_DARK,
    paddingTop: 80,
    paddingBottom: 52,
    paddingHorizontal: 42,
  },
  // The header is fixed and repeats on every page: full on page 1, a compact
  // running header on later pages. Page paddingTop reserves room for it.
  headerBand: {
    position: 'absolute',
    top: 24,
    left: 42,
    right: 42,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 8,
    borderBottom: `2px solid ${BRAND_RED}`,
  },
  logoImage: {
    width: 130,
    height: 20,
  },
  logoImageSmall: {
    width: 95,
    height: 15,
  },
  runningHeaderText: {
    fontSize: 8,
    color: TEXT_GRAY,
  },
  proposalTitleRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'baseline',
  },
  proposalTitleBase: {
    fontSize: 15,
    fontFamily: 'Helvetica-Bold',
  },
  proposalMeta: {
    fontSize: 8,
    color: TEXT_GRAY,
    textAlign: 'right',
    marginTop: 4,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  infoBox: {
    flex: 1,
  },
  infoBoxRight: {
    flex: 1,
    paddingLeft: 24,
  },
  sectionHeading: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: BRAND_RED,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
    paddingBottom: 3,
    borderBottom: `1px solid ${BRAND_RED}`,
  },
  infoLine: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  infoLabel: {
    width: 72,
    fontSize: 8,
    color: TEXT_GRAY,
  },
  infoValue: {
    flex: 1,
    fontSize: 8,
    color: TEXT_DARK,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: BRAND_RED,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginTop: 6,
  },
  tableHeaderCell: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    color: '#ffffff',
  },
  colProduct: {
    width: '22%',
  },
  colConfig: {
    width: '58%',
    paddingLeft: 4,
  },
  colAmount: {
    width: '20%',
    textAlign: 'right',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottom: `1px solid ${BORDER_GRAY}`,
    alignItems: 'flex-start',
  },
  tableRowAlt: {
    backgroundColor: BG_ALT_ROW,
  },
  tableCell: {
    fontSize: 8,
    color: TEXT_DARK,
  },
  tableCellConfig: {
    fontSize: 7.5,
    color: TEXT_GRAY,
    marginBottom: 2,
  },
  tableCellConfigLast: {
    fontSize: 7.5,
    color: TEXT_GRAY,
    marginBottom: 0,
  },
  totalRow: {
    flexDirection: 'row',
    paddingVertical: 7,
    paddingHorizontal: 8,
    backgroundColor: BG_LIGHT,
    borderTop: `2px solid ${BRAND_RED}`,
  },
  totalSpacer: {
    flex: 1,
  },
  totalLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: TEXT_DARK,
    textAlign: 'right',
    paddingRight: 4,
    width: '58%',
  },
  totalAmount: {
    width: '20%',
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: BRAND_RED,
    textAlign: 'right',
  },
  termsBox: {
    marginTop: 18,
    padding: 12,
    backgroundColor: BG_LIGHT,
    border: `1px solid ${BORDER_GRAY}`,
  },
  assumptionsBox: {
    marginTop: 10,
    padding: 12,
    backgroundColor: BG_LIGHT,
    border: `1px solid ${BORDER_GRAY}`,
  },
  basisBox: {
    marginTop: 18,
    padding: 12,
    backgroundColor: BG_LIGHT,
    border: `1px solid ${BORDER_GRAY}`,
  },
  basisLine: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  basisLabel: {
    width: 120,
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
  },
  basisValue: {
    flex: 1,
    fontSize: 7.5,
    color: TEXT_GRAY,
  },
  basisNote: {
    marginTop: 6,
    fontSize: 7.5,
    color: TEXT_GRAY,
    lineHeight: 1.5,
  },
  termsHeading: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 7,
    color: TEXT_DARK,
  },
  assumptionGroup: {
    marginTop: 8,
  },
  assumptionGroupHeading: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: BRAND_RED,
    marginBottom: 4,
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  bulletMark: {
    fontSize: 7.5,
    color: BRAND_RED,
    width: 10,
    marginTop: 0.5,
  },
  bulletText: {
    flex: 1,
    fontSize: 7.5,
    color: TEXT_GRAY,
    lineHeight: 1.5,
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 42,
    right: 42,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTop: `1px solid ${BORDER_GRAY}`,
    paddingTop: 6,
  },
  footerText: {
    fontSize: 7,
    color: '#969696',
  },
});

const formatCurrency = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface InfoLineProps {
  label: string;
  value: string;
}

const InfoLine: React.FC<InfoLineProps> = ({ label, value }) => (
  <View style={styles.infoLine}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoValue}>{value}</Text>
  </View>
);

// One scope line as plain English, e.g. "12 reports per pipe, Standard data
// complexity, 1,200 pipes". Only the parts the breakdown carries are included.
const formatScopeLine = (line: ScopeSummaryLine): string => {
  if (line.description) return line.description;
  const parts: string[] = [];
  if (line.reportCount !== undefined && line.reportUnit) {
    const noun = line.reportCount === 1 ? 'report' : 'reports';
    parts.push(`${line.reportCount.toLocaleString('en-US')} ${noun} ${line.reportUnit}`);
  }
  if (line.complexity) {
    parts.push(`${line.complexity} data complexity`);
  }
  if (line.volume !== undefined && line.volumeUnit) {
    parts.push(`${line.volume.toLocaleString('en-US')} ${line.volumeUnit}`);
  }
  return parts.join(', ');
};

interface PricingSheetPDFDocumentProps {
  data: PricingSheetData;
}

export const PricingSheetPDFDocument: React.FC<PricingSheetPDFDocumentProps> = ({ data }) => {
  const addressParts = [
    data.addressLine1,
    data.addressLine2,
    [data.city, data.state, data.postalCode].filter(Boolean).join(', '),
    data.country,
  ].filter(Boolean);

  const termsBullets = [
    `This proposal is offered for acceptance for ${data.validityPeriod || '30 days'} from the date of issue.`,
    'All hardware provided under this agreement is rented to the customer and remains the property of CRC Evans. Hardware must be returned in good working condition upon completion of the project. Charges may apply for loss or damage.',
    'All intellectual property related to the DATA 360 platform, including software, documentation, and configurations, remains the sole property of CRC Evans.',
    'CRC Evans reserves the right to use anonymized and aggregated data collected during the course of the project for the purpose of improving the functionality, performance, and user experience of the DATA 360 platform. No customer-identifiable information or project-sensitive content will be shared or disclosed without prior written consent.',
    'The prices set forth in this Proposal or any related Purchase Order are exclusive of all taxes, duties, levies, tariffs, and other governmental charges, including but not limited to sales, use, excise, value-added, goods and services, withholding, or similar taxes imposed by any jurisdiction. All such taxes shall be the sole responsibility of the customer, unless the customer provides a valid tax exemption certificate acceptable to the relevant taxing authority.',
  ];

  const assumptionSections = buildAssumptionSections(data.moduleIds);

  // Per-module scope lines that have something to show, plus the duration and
  // user count. The whole block is omitted when there is nothing to state.
  const scopeLines = data.scope.lines
    .map(line => ({ productName: line.productName, text: formatScopeLine(line) }))
    .filter(line => line.text.length > 0);
  const showDuration = data.scope.durationMonths > 0;
  const showUsers = data.scope.numberOfUsers > 0;
  const showBasis = scopeLines.length > 0 || showDuration || showUsers;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header: repeats on every page, full on page 1, compact afterwards. */}
        <View
          style={styles.headerBand}
          fixed
          render={({ pageNumber }) =>
            pageNumber === 1 ? (
              <>
                <Image src={logo} style={styles.logoImage} />
                <View>
                  <View style={styles.proposalTitleRow}>
                    <Text style={[styles.proposalTitleBase, { color: '#EB1C37' }]}>DATA </Text>
                    <Text style={[styles.proposalTitleBase, { color: '#969696' }]}>360 </Text>
                    <Text style={[styles.proposalTitleBase, { color: TEXT_DARK }]}>PRICE SHEET</Text>
                  </View>
                  <Text style={styles.proposalMeta}>Date: {data.date}   |   Ref: {data.referenceNumber}</Text>
                </View>
              </>
            ) : (
              <>
                <Image src={logo} style={styles.logoImageSmall} />
                <Text style={styles.runningHeaderText}>
                  DATA 360 Price Sheet   |   Ref: {data.referenceNumber}
                </Text>
              </>
            )
          }
        />

        {/* Two-column info section */}
        <View style={styles.infoRow}>
          <View style={styles.infoBox}>
            <Text style={styles.sectionHeading}>Prepared For</Text>
            {data.customerName ? <InfoLine label="Customer:" value={data.customerName} /> : null}
            {data.customerAttn ? <InfoLine label="Attention:" value={data.customerAttn} /> : null}
            {data.customerEmail ? <InfoLine label="Email:" value={data.customerEmail} /> : null}
            {data.customerPhone ? <InfoLine label="Phone:" value={data.customerPhone} /> : null}
            {addressParts.map((line, i) => (
              <InfoLine key={i} label={i === 0 ? 'Address:' : ''} value={line} />
            ))}
          </View>

          <View style={styles.infoBoxRight}>
            <Text style={styles.sectionHeading}>Project Details</Text>
            {data.projectTitle ? <InfoLine label="Project:" value={data.projectTitle} /> : null}
            {data.startDate ? <InfoLine label="Start:" value={data.startDate} /> : null}
            {data.endDate ? <InfoLine label="End:" value={data.endDate} /> : null}
            {data.durationLabel ? <InfoLine label="Duration:" value={data.durationLabel} /> : null}
            {data.paymentTerms ? <InfoLine label="Payment:" value={data.paymentTerms} /> : null}
            {data.validityPeriod ? <InfoLine label="Valid for:" value={data.validityPeriod} /> : null}
          </View>
        </View>

        {/* Products table */}
        <Text style={styles.sectionHeading}>Selected Products & Services</Text>

        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, styles.colProduct]}>Product / Service</Text>
          <Text style={[styles.tableHeaderCell, styles.colConfig]}>Configuration</Text>
          <Text style={[styles.tableHeaderCell, styles.colAmount]}>Amount</Text>
        </View>

        {data.products.map((row, idx) => (
          <View key={idx} style={[styles.tableRow, idx % 2 === 1 ? styles.tableRowAlt : {}]} wrap={false}>
            <Text style={[styles.tableCell, styles.colProduct]}>{row.productName}</Text>
            {/* Each config item on its own line */}
            <View style={styles.colConfig}>
              {row.configuration.map((line, lineIdx) => (
                <Text
                  key={lineIdx}
                  style={lineIdx < row.configuration.length - 1 ? styles.tableCellConfig : styles.tableCellConfigLast}
                >
                  {line}
                </Text>
              ))}
            </View>
            <Text style={[styles.tableCell, styles.colAmount]}>{formatCurrency(row.amount)}</Text>
          </View>
        ))}

        <View style={styles.totalRow} wrap={false}>
          <View style={styles.totalSpacer} />
          <Text style={styles.totalLabel}>TOTAL</Text>
          <Text style={styles.totalAmount}>{formatCurrency(data.grandTotal)}</Text>
        </View>

        {/* Terms & Conditions */}
        <View style={styles.termsBox}>
          <Text style={styles.termsHeading}>Terms & Conditions</Text>
          {termsBullets.map((bullet, i) => (
            <View key={i} style={styles.bulletRow} wrap={false}>
              <Text style={styles.bulletMark}>»</Text>
              <Text style={styles.bulletText}>{bullet}</Text>
            </View>
          ))}
        </View>

        {/* Assumptions */}
        <View style={styles.assumptionsBox}>
          <Text style={styles.termsHeading}>Assumptions</Text>
          {assumptionSections.map((section, si) => (
            <View key={si} style={si > 0 ? styles.assumptionGroup : {}} wrap={false}>
              <Text style={styles.assumptionGroupHeading}>{section.heading}</Text>
              {section.bullets.map((bullet, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletMark}>»</Text>
                  <Text style={styles.bulletText}>{bullet}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        {/* Basis of this quote */}
        {showBasis && (
          <View style={styles.basisBox} wrap={false}>
            <Text style={styles.termsHeading}>Basis of this quote</Text>
            {scopeLines.map((line, i) => (
              <View key={i} style={styles.basisLine}>
                <Text style={styles.basisLabel}>{line.productName}</Text>
                <Text style={styles.basisValue}>{line.text}</Text>
              </View>
            ))}
            {showDuration && (
              <View style={styles.basisLine}>
                <Text style={styles.basisLabel}>Project duration</Text>
                <Text style={styles.basisValue}>{data.scope.durationMonths} months</Text>
              </View>
            )}
            {showUsers && (
              <View style={styles.basisLine}>
                <Text style={styles.basisLabel}>Platform users</Text>
                <Text style={styles.basisValue}>{data.scope.numberOfUsers}</Text>
              </View>
            )}
            <Text style={styles.basisNote}>
              Pricing is based on these figures. Material changes to scope, volume, or
              duration will be re-quoted.
            </Text>
            <Text style={styles.basisNote}>
              All amounts are in USD. Quote validity and tax treatment are set out in the
              terms below.
            </Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>CRC Evans - Confidential</Text>
          <Text style={styles.footerText}>{data.referenceNumber}</Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
};

export async function generatePricingSheetPDFBlob(data: PricingSheetData): Promise<Blob> {
  return pdf(<PricingSheetPDFDocument data={data} />).toBlob();
}

export async function downloadPricingSheetPDF(data: PricingSheetData): Promise<void> {
  const blob = await generatePricingSheetPDFBlob(data);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Pricing-Proposal-${data.referenceNumber}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
